import { BRAND_CODE } from "@/config/common";
import type { MarketConfiguration } from "@/config/markets/const";
import type {
  CustomProduct,
  CustomProductCustom,
  CustomProductCustomJson,
  CustomProductCustomLengths,
  CustomProductCustomNumbers,
} from "@apptus/esales-api";
import type { Image } from "types";
import getNorceCdnImageUrl from "./getNorceCdnImageUrl";

export type ProductConfig = {
  hasVariants?: boolean;
  allowDefault?: boolean;
  promoteProductImages?: boolean;
};

/**
 * Type-safe helper for retrieving the keys of an object as typed key array.
 *
 * @param obj Source object
 * @returns Array of the object's keys with proper keyof typing
 */
function getTypedKeys<T extends object>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[];
}

/**
 * @deprecated use mapProductImages instead
 *
 * Collects product images from primary sources and Norce CDN fallback keys,
 * deduplicates by URL, and returns normalized `Image` objects.
 *
 * @param product Elevate product containing image info and custom image keys
 * @param market Market configuration for Norce CDN URL generation
 * @returns Unique list of images with `src`, `alt`, `id`, and `type`
 */
export function getImagesOfProduct(
  product: CustomProduct,
  market: MarketConfiguration,
): Image[] {
  const primaryImages = product.imageInfo.images;
  const imageKeys = product.typedCustom?.json.imagesKey ?? [];
  const map = new Map<string, Image>();
  for (const image of primaryImages) {
    const [source] = image.sources;
    if (!source) continue;
    map.set(source.url, {
      src: source.url ?? "",
      alt: image.alt ?? image.caption ?? product.title,
      id: product.key + "_image",
      type: "image",
    });
  }
  for (const imageKey of imageKeys) {
    if (map.has(imageKey)) continue;
    map.set(imageKey, {
      src: getNorceCdnImageUrl(imageKey, market),
      alt: product.title,
      id: product.key + "_image_" + imageKey,
      type: "image",
    });
  }
  return [...map.values()];
}

/**
 * Checks if an attribute key is market-specific (prefixed or suffixed).
 *
 * @param attribute Attribute key to check
 * @returns True if key targets a specific market (e.g., contains brand/market code)
 */
export function isMarketSpecificAttribute(attribute: string) {
  return attribute.includes("NG_") || attribute.includes("LM_");
}
/**
 * Normalizes a market-specific attribute key by stripping a pattern prefix or suffix.
 *
 * @param attribute Attribute key to normalize
 * @param pattern Pattern to remove (e.g., BRANDCODE_market)
 * @returns Attribute key without the market-specific pattern
 */
export function getMarketSpecificAttribute(attribute: string, pattern: string) {
  if (attribute.startsWith(pattern)) {
    return attribute.replace(pattern + "_", "");
  }
  return attribute.replace("_" + pattern, "");
}

/**
 * Derives price information for a product variant, including campaign and
 * market-specific price entries.
 *
 * @param product Elevate product containing variant prices
 * @param marketConfig Market configuration used to interpret price ids
 * @returns Price details including `price`, `campaignPrice`, `priceType`, and mapped prices
 */
export function getPriceFromVariant(
  product: CustomProduct,
  marketConfig: MarketConfiguration,
) {
  const { code } = marketConfig;
  const prefix = "NG_" + code + "_";
  const suffix = "_NG_" + code;
  const [variant] = product.variants;
  const priceBest = variant?.prices.find(
    (price) => price.id === prefix + "best",
  );
  const price = priceBest?.sellingPrice ?? variant?.sellingPrice ?? 0;
  const price30 = priceBest?.listPrice ?? variant?.listPrice ?? price;
  const priceRrp = product.typedCustom?.numbers?.["rrp" + suffix] ?? price;

  const prices = variant?.prices;

  let priceType = "standard";
  if (prices?.some((price) => price.id === prefix + "campaign")) {
    priceType = "campaign";
  } else if (prices?.some((price) => price.id === prefix + "pricematch")) {
    priceType = "priceMatch";
  } else if (prices?.some((price) => price.id === prefix + "RRP")) {
    priceType = "standard";
  }

  return {
    price,
    price30,
    priceRrp,
    priceType,
  };
}

/**
 * Flattens variant-defining attributes into a simple array shape
 * suitable for downstream consumption.
 *
 * @param product Elevate product with variant-defining attributes
 * @returns Array of flattened variant attribute descriptors
 */
export function flattenVariantAttributes(product: CustomProduct) {
  const variantAttributes =
    product.typedCustom?.json.variantDefiningVariantAttributes;
  if (!variantAttributes) return [];
  return Object.entries(variantAttributes).map(([key, value]) => ({
    key,
    Label: value.label,
    Values: [value.value],
  }));
}

/**
 * Flattens `custom` string attributes, resolving market-specific keys based
 * on the provided market configuration.
 *
 * @param product Elevate product with `custom` attributes
 * @param marketConfig Market configuration to resolve market-specific keys
 * @returns Object map of flattened custom string attributes
 */
export function flattenCustomAttributes<T extends boolean = false>(
  product: CustomProduct,
  marketConfig: MarketConfiguration,
  singleValues?: T,
): Record<keyof CustomProductCustom, T extends true ? string : string[]> & {
  fromPriceType: string;
  fromPriceSame: string;
} {
  const custom = product.custom ?? {};
  return Object.fromEntries(
    Object.entries(custom).map(([key, value]) => {
      if (isMarketSpecificAttribute(key)) {
        const pattern = BRAND_CODE + "_" + marketConfig.code.toLowerCase();
        if (key.includes(pattern)) {
          const partialKey = getMarketSpecificAttribute(key, pattern);
          if (singleValues && value.length <= 1) {
            return [partialKey, value[0].id];
          }
          return [partialKey, value.flatMap((v) => v.id)];
        } else if (key.includes("defaultProduct")) {
          return [key, [value[0].id]];
        }
        return [];
      }
      if (singleValues && value.length <= 1) {
        return [key, value[0].id];
      }
      return [key, value.flatMap((v) => v.id)];
    }),
  );
}

/**
 * Flattens numeric length-based attributes from `typedCustom.lengths`,
 * normalizing market-specific keys when applicable.
 *
 * @param product Elevate product with `lengths` custom attributes
 * @param marketConfig Market configuration to normalize keys
 * @returns Object map of flattened numeric length attributes
 */
export function flattenCustomLengths(
  product: CustomProduct,
  marketConfig: MarketConfiguration,
): Record<keyof CustomProductCustomLengths, number> {
  const custom = product.typedCustom?.lengths ?? {};
  return Object.fromEntries(
    Object.entries(custom).map(([key, value]) => {
      if (isMarketSpecificAttribute(key)) {
        const pattern = BRAND_CODE + "_" + marketConfig.code.toLowerCase();
        if (key.includes(pattern)) {
          const partialKey = getMarketSpecificAttribute(key, pattern);
          return [partialKey, value.amount];
        }
        return [];
      }
      return [key, value.amount];
    }),
  );
}

/**
 * Flattens numeric attributes from `typedCustom.numbers`,
 * normalizing market-specific keys when applicable and augmenting
 * with common numeric fields used downstream.
 *
 * @param product Elevate product with `numbers` custom attributes
 * @param marketConfig Market configuration to normalize keys
 * @returns Object map of flattened numeric attributes with additional fields
 */
export function flattenCustomNumbers(
  product: CustomProduct,
  marketConfig: MarketConfiguration,
): Record<keyof CustomProductCustomNumbers, number> & {
  rrp: number;
  vat: number;
  cmp30: number;
  fromPrice: number;
  fromPriceRRP: number;
  fromPrice30Days: number;
} {
  const custom = product.typedCustom?.numbers ?? {};
  return Object.fromEntries(
    Object.entries(custom).map(([key, value]) => {
      if (isMarketSpecificAttribute(key)) {
        const pattern = BRAND_CODE + "_" + marketConfig.code.toLowerCase();
        if (key.includes(pattern)) {
          const partialKey = getMarketSpecificAttribute(key, pattern);
          return [partialKey, value];
        }
        return [];
      }
      return [key, value];
    }),
  );
}

function mergeRelationMaps(
  ...sources: Array<CustomProductCustomJson["productRelations"]>
) {
  const merged: Record<string, string[]> = {};
  for (const source of sources) {
    if (!source) continue;
    for (const [key, values] of Object.entries(source)) {
      merged[key] = Array.from(
        new Set([...(merged[key] ?? []), ...(values ?? [])]),
      );
    }
  }
  return merged;
}

/**
 * Flattens the `typedCustom.json` payload into a shallow key-value map,
 * converting nested structures (attributes, relations, designers, media keys)
 * into consumable arrays and primitives.
 *
 * @param product Elevate product with `typedCustom.json`
 * @returns Flattened JSON map including relevant nested fields
 *
 * I don't like this mapping thing. I thing a better approach would be to get the data as we want it from backend.
 */
export function flattenCustomJson(product: CustomProduct) {
  const output: CustomProductCustomJson & Record<string, string | string[]> =
    {};
  const custom = product.typedCustom?.json;
  if (!custom) return {};
  for (const key of getTypedKeys(custom as CustomProductCustomJson)) {
    switch (key) {
      case "productAttributes":
      case "variantAttributes": {
        const nestedObject = Object.fromEntries(
          Object.entries(custom[key] ?? {}).map(([key, value]) => {
            if (value.values) {
              return [key, value.values.map((v: any) => v.value)];
            }
            return [key, value.value];
          }),
        );
        Object.assign(output, nestedObject);
        break;
      }
      case "productRelations":
      case "variantRelations": {
        Object.assign(
          output,
          mergeRelationMaps(custom.variantRelations, custom.productRelations),
        );
        break;
      }
      case "variantDefiningAttributes":
        break;
      case "variantDefiningVariantAttributes":
        Object.assign(
          output,
          Object.fromEntries(
            Object.entries(custom.variantDefiningVariantAttributes ?? {}).map(
              ([key, value]) => [key, value.value],
            ),
          ),
        );
        output.variantDefiningVariantAttributes =
          custom.variantDefiningVariantAttributes;
        break;
      case "designers": {
        Object.assign(
          output,
          Object.fromEntries(
            Object.entries(custom.designers ?? {}).map(([key, value]) => [
              key,
              value.Value,
            ]),
          ),
        );
        break;
      }
      case "priceListIds": {
        break;
      }
      case "structureItems": {
        output.structureItems = custom.structureItems;
        break;
      }
      case "datasheetsKey":
      case "modelsKey":
      case "videosKey":
      case "imagesKey": {
        output[key] = custom[key];
        break;
      }
      case "v2_productMainImageKey":
      case "v2_variantMainImageKey": {
        output[key] = (custom[key] ?? undefined) as string | undefined;
        break;
      }
      case "mainProductURL":
      case "productImageURLKey":
      case "variantName":
      case "variantType": {
        output[key] = custom[key];
        break;
      }
      default:
        output[key] = [custom[key]].flat(2) as any; // why?
        break;
    }
  }
  return output;
}
